// Pure, store-agnostic reducers over a `SpatialViewDefinition` (T16). Every layer edit in the
// Spatial Explorer flows through one of these so the tab-content update in `SpatialExplorerTab`
// stays a one-liner (`updateTabContent(tabId, { view })`). No React, no maplibre, no store import —
// unit-tested in isolation and safe to reuse from T17's session/entry-point work.

import { SPATIAL_LAYER_PALETTE, layerColorAt } from '../../spatial/map/mapThemes';
import { DEFAULT_SPATIAL_FEATURE_CAP } from '../../spatial/types/spatialTypes';
import type {
  SpatialLayerDefinition,
  SpatialLayerSource,
  SpatialViewDefinition,
} from '../../spatial/types/spatialTypes';

/** Hard caps for a per-layer feature limit (spec §3.3 default is 10 000). */
export const MIN_SPATIAL_FEATURE_CAP = 1;
export const MAX_SPATIAL_FEATURE_CAP = 100_000;

/** An empty Spatial Explorer view — the zero-layer state a fresh tab starts in. */
export function createEmptyView(): SpatialViewDefinition {
  return { layers: [], basemap: 'default' };
}

let layerSeq = 0;
/** Monotonic, collision-resistant layer id (never persisted-by-value like the palette). */
export function nextLayerId(): string {
  layerSeq += 1;
  return `spatial-layer-${Date.now().toString(36)}-${layerSeq.toString(36)}`;
}

/**
 * The first `SPATIAL_LAYER_PALETTE` colour not already used by a layer; once every palette entry is
 * in use it cycles via `layerColorAt` (never a hardcoded hex — mapThemes owns all spatial colour).
 */
export function nextLayerColor(layers: readonly SpatialLayerDefinition[]): string {
  const used = new Set(layers.map((l) => l.style.color));
  for (const color of SPATIAL_LAYER_PALETTE) {
    if (!used.has(color)) return color;
  }
  return layerColorAt(layers.length);
}

/** Clamp a user-entered feature cap into the allowed range (NaN / non-finite → the default). */
export function clampFeatureCap(cap: number): number {
  if (!Number.isFinite(cap)) return DEFAULT_SPATIAL_FEATURE_CAP;
  return Math.min(MAX_SPATIAL_FEATURE_CAP, Math.max(MIN_SPATIAL_FEATURE_CAP, Math.trunc(cap)));
}

/** Build a layer definition for a given source, taking the next unused palette colour. */
export function makeLayer(params: {
  source: SpatialLayerSource;
  label: string;
  existing: readonly SpatialLayerDefinition[];
  labelColumn?: string;
  id?: string;
}): SpatialLayerDefinition {
  return {
    id: params.id ?? nextLayerId(),
    label: params.label,
    visible: true,
    source: params.source,
    labelColumn: params.labelColumn,
    style: { color: nextLayerColor(params.existing) },
    featureCap: DEFAULT_SPATIAL_FEATURE_CAP,
  };
}

/** Append a layer. */
export function addLayer(
  view: SpatialViewDefinition,
  layer: SpatialLayerDefinition,
): SpatialViewDefinition {
  return { ...view, layers: [...view.layers, layer] };
}

/** Remove a layer by id. */
export function removeLayer(view: SpatialViewDefinition, layerId: string): SpatialViewDefinition {
  return { ...view, layers: view.layers.filter((l) => l.id !== layerId) };
}

/** Patch a single layer (shallow-merge). */
export function updateLayer(
  view: SpatialViewDefinition,
  layerId: string,
  patch: Partial<SpatialLayerDefinition>,
): SpatialViewDefinition {
  return {
    ...view,
    layers: view.layers.map((l) => (l.id === layerId ? { ...l, ...patch } : l)),
  };
}

export function setLayerVisibility(
  view: SpatialViewDefinition,
  layerId: string,
  visible: boolean,
): SpatialViewDefinition {
  return updateLayer(view, layerId, { visible });
}

export function setLayerColor(
  view: SpatialViewDefinition,
  layerId: string,
  color: string,
): SpatialViewDefinition {
  const layer = view.layers.find((l) => l.id === layerId);
  if (!layer) return view;
  return updateLayer(view, layerId, { style: { ...layer.style, color } });
}

export function setLayerLabelColumn(
  view: SpatialViewDefinition,
  layerId: string,
  labelColumn: string | undefined,
): SpatialViewDefinition {
  return updateLayer(view, layerId, { labelColumn });
}

/** Set a layer's feature cap, clamped into range. */
export function setLayerCap(
  view: SpatialViewDefinition,
  layerId: string,
  cap: number,
): SpatialViewDefinition {
  return updateLayer(view, layerId, { featureCap: clampFeatureCap(cap) });
}

/** Move a layer up or down in draw order (no-op at the boundary). */
export function reorderLayer(
  view: SpatialViewDefinition,
  layerId: string,
  direction: 'up' | 'down',
): SpatialViewDefinition {
  const index = view.layers.findIndex((l) => l.id === layerId);
  if (index === -1) return view;
  const target = direction === 'up' ? index - 1 : index + 1;
  if (target < 0 || target >= view.layers.length) return view;
  const layers = [...view.layers];
  [layers[index], layers[target]] = [layers[target], layers[index]];
  return { ...view, layers };
}
